// Copyright 2019 Sorama B.V.

import { BindingEngine, Disposable, ObserverLocator } from "aurelia-binding";
import { IWorkflowModuleReference } from "../Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "../Interfaces/IWorkflowPropertyMetadata";
import { ScopedWorkflowPropertyObserver, WorkflowPropertyObserver } from "./WorkflowPropertyObserver";

export class ScopedWorkflowPropertyIterator<T> implements Iterator<T> {
    private properties: string[] = [];
    private count = 0;

    constructor(private readonly property: ScopedWorkflowProperty<T>) {
        for (const care in this.property) {
            this.properties.push(care);
        }
    }

    public next(): IteratorResult<T> {
        if (this.count >= this.properties.length) {
            return <IteratorResult<T>>{
                done: true,
            };
        }
        return <IteratorResult<T>>{
            value: this.property[this.count++],
        };
    }
}

export class ScopedWorkflowProperty<T> implements Iterable<T> {
    private observers: (() => void)[] = [];
    public observedModuleIds: number[] = [];

    [Symbol.iterator](): Iterator<T> {
        const iterator = this.observedModuleIds[Symbol.iterator]();
        return <Iterator<T>>{
            next: () => {
                const arrayIterator = iterator.next();
                return <IteratorResult<T>>{
                    done: arrayIterator.done,
                    value: arrayIterator.value ? this[arrayIterator.value] : undefined,
                };
            },
        };
    }

    public has(moduleId: number): boolean {
        return this.observedModuleIds.findIndex((x) => x == moduleId) != -1;
    }

    public get(moduleId: number): T {
        return this[moduleId];
    }

    public add(moduleId: number, property: T): void {
        this[moduleId] = property;
        if (!this.has(moduleId)) {
            this.observedModuleIds.push(moduleId);
        }
        this.notifyObservers();
    }

    public remove(moduleId: number): void {
        if (this.observedModuleIds.findIndex((x) => x == moduleId) == -1) {
            // Nothing to remove...
            return;
        }

        if (this[moduleId] != undefined) {
            delete this[moduleId];
        }

        this.observedModuleIds.splice(
            this.observedModuleIds.findIndex((x) => x == moduleId),
            1
        );
        this.notifyObservers();
    }

    public addObserver(callback: () => void): Disposable {
        if (!this.observers) this.observers = [];

        this.observers.push(callback);

        return <Disposable>{
            dispose: () => {
                if (this.observers) {
                    const idx = this.observers.findIndex((x) => x == callback);
                    if (idx != -1) {
                        this.observers.splice(idx, 1);
                    }
                }
            },
        };
    }

    /**
     * Checks if any observer property contains the indicated value
     * @param val S
     */
    public hasVal(callback: (val: T) => boolean): boolean {
        for (const idx of this.observedModuleIds) {
            const mod = this.get(idx);
            if (callback(mod)) return true;
        }
    }

    [moduleId: number]: T;

    private notifyObservers(): void {
        if (this.observers) {
            for (const observer of this.observers) {
                try {
                    observer();
                } catch (e) {
                    // Print the error and continue the flow
                    console.warn(e);
                }
            }
        }
    }
}

/**
 * This observer only support one-to-many relations
 */
export class ModuleSetScopeObserver {
    private subscribers: (() => void)[] = [];
    private moduleObserver: Disposable;
    private modulePropertyCollectionObserver: Disposable;

    public get workflowPropertyObservers(): WorkflowPropertyObserver[] {
        return this._workflowPropertyObservers.map((x) => x.observer);
    }

    private readonly _workflowPropertyObservers: { observer: WorkflowPropertyObserver; disposable: Disposable }[] = [];

    public get targetModuleIds(): number[] {
        const result: number[] = [];

        for (const observer of this.workflowPropertyObservers) result.push(...observer.workflowModuleTargetIds);

        return result.filter((x) => x != this.masterModule.moduleId);
    }

    constructor(
        private readonly bindingEngine: BindingEngine,
        private readonly masterModule: IWorkflowModuleReference,
        public readonly propertyName: string,
        private readonly masterBindingMode: WorkflowPropertyBindingMode,
        private readonly propertyType: string,
        private readonly locator: ObserverLocator,
        private readonly defaultValue: unknown = undefined
    ) {
        const observeCollection = () => {
            if (this.modulePropertyCollectionObserver) this.modulePropertyCollectionObserver.dispose();

            if (this.masterModule[propertyName] != undefined) {
                const scopedProperty: ScopedWorkflowProperty<unknown> = this.masterModule[propertyName];
                this.modulePropertyCollectionObserver = scopedProperty.addObserver(() => {
                    this.updateObserver();
                });

                this.updateObserver();
            }
        };

        this.moduleObserver = this.bindingEngine
            .propertyObserver(this.masterModule, this.propertyName)
            .subscribe(() => {
                observeCollection();
            });

        if (this.masterModule[propertyName] == undefined) {
            this.masterModule[propertyName] = new ScopedWorkflowProperty<unknown>();
        }

        // Make sure that nobody ruins this property by making it read only
        const prop: ScopedWorkflowProperty<unknown> = this.masterModule[propertyName];

        Object.defineProperty(this.masterModule, propertyName, {
            get: () => {
                return prop;
            },
        });

        observeCollection();
    }

    public addModule(module: IWorkflowModuleReference): void {
        const observer = this.workflowPropertyObservers.find(
            (x) => x.workflowModuleTargetIds.findIndex((y) => y == module.moduleId) != -1
        );

        if (observer != undefined && observer.getModuleById(module.moduleId) != undefined) {
            throw new Error("Module is already observed. It is not possible to observe it twice");
        } else if (observer != undefined) {
            observer.addModule(module);
        } else {
            const prop: ScopedWorkflowProperty<unknown> = this.masterModule[this.propertyName];

            // Unable to find a observer, so add a new one
            const propertyObserver = new ScopedWorkflowPropertyObserver(
                this.bindingEngine,
                this.propertyName,
                this.propertyType,
                this.locator,
                this.masterModule,
                this.masterBindingMode,
                module.moduleId,
                this.defaultValue
            );

            this._workflowPropertyObservers.push({
                observer: propertyObserver,
                disposable: propertyObserver.subscribe(() => {
                    this.notifySubscribers();
                }),
            });

            prop.add(module.moduleId, module[this.propertyName]);

            propertyObserver.addModule(module);
        }
    }

    public removeModule(moduleId: number): void {
        if (moduleId == this.masterModule.moduleId) {
            throw new Error("It is not possible to remove the master module");
        }

        const observer = this._workflowPropertyObservers.find(
            (x) => x.observer.workflowModuleTargetIds.findIndex((y) => y == moduleId) != -1
        );

        if (observer == undefined) {
            throw new Error("Module is not found");
        }

        observer.observer.dispose();
        observer.disposable.dispose();

        const idx = this._workflowPropertyObservers.findIndex((x) => x == observer);
        this._workflowPropertyObservers.splice(idx, 1);
    }

    public dispose(): void {
        if (this.moduleObserver) {
            this.moduleObserver.dispose();
        }

        if (this.modulePropertyCollectionObserver) {
            this.modulePropertyCollectionObserver.dispose();
        }

        while (this._workflowPropertyObservers.length > 0) {
            const observer = this._workflowPropertyObservers.pop();
            observer.observer.dispose();
            observer.disposable.dispose();
        }
    }

    public subscribe(callback: () => void): Disposable {
        this.subscribers.push(callback);

        return <Disposable>{
            dispose: () => {
                const idx = this.subscribers.findIndex((x) => x == callback);
                if (idx != -1) this.subscribers.splice(idx, 1);
            },
        };
    }

    private updateObserver(): void {
        const prop: ScopedWorkflowProperty<unknown> = this.masterModule[this.propertyName];
        for (const idx of prop.observedModuleIds) {
            if (
                !this.workflowPropertyObservers.find((x) => x.workflowModuleTargetIds.findIndex((y) => y == idx) > -1)
            ) {
                // Unable to find a observer, so add a new one
                const propertyObserver = new ScopedWorkflowPropertyObserver(
                    this.bindingEngine,
                    this.propertyName,
                    this.propertyType,
                    this.locator,
                    this.masterModule,
                    this.masterBindingMode,
                    idx,
                    this.defaultValue
                );

                this._workflowPropertyObservers.push({
                    observer: propertyObserver,
                    disposable: propertyObserver.subscribe(() => {
                        this.notifySubscribers();
                    }),
                });
            }
        }

        this.notifySubscribers();
    }

    private notifySubscribers(): void {
        for (const subscriber of this.subscribers) {
            subscriber();
        }
    }
}
